import React from 'react';

import {
  Building2,
  ChevronRight,
  Landmark,
  Megaphone,
  Package,
  Receipt,
  Settings,
  Users,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../components/common/Card';
import { AiScoringAssistSettings } from '../../components/settings/AiScoringAssistSettings';
import { DataSharingConsentSettings } from '../../components/settings/DataSharingConsentSettings';
import { APP_PERMISSIONS, ROUTES } from '../../constants';
import { usePermissions } from '../../hooks/usePermissions';

interface AdministrationArea {
  name: string;
  description: string;
  href: string;
  permission: string;
  icon: React.ComponentType<{ className?: string }>;
}

const administrationAreas: AdministrationArea[] = [
  {
    name: 'Users and Roles',
    description: 'Create accounts, assign roles, and manage account access.',
    href: ROUTES.USERS,
    permission: APP_PERMISSIONS.READ_USER,
    icon: Users,
  },
  {
    name: 'Schools',
    description: 'Maintain school records and their district assignments.',
    href: ROUTES.SCHOOLS,
    permission: APP_PERMISSIONS.READ_SCHOOL,
    icon: Building2,
  },
  {
    name: 'Districts',
    description: 'Review and maintain district organization records.',
    href: ROUTES.DISTRICTS,
    permission: APP_PERMISSIONS.READ_DISTRICT,
    icon: Landmark,
  },
  {
    name: 'Budget Planning',
    description: 'Review allocations, spending, variances, and forecasts.',
    href: ROUTES.BUDGET,
    permission: APP_PERMISSIONS.VIEW_BUDGET,
    icon: Receipt,
  },
  {
    name: 'Inventory',
    description: 'Manage district assets, assignments, and condition.',
    href: ROUTES.INVENTORY,
    permission: APP_PERMISSIONS.MANAGE_RESOURCES,
    icon: Package,
  },
  {
    name: 'Announcements',
    description: 'Create and manage district and school communications.',
    href: ROUTES.ANNOUNCEMENTS,
    permission: APP_PERMISSIONS.MANAGE_COMMUNICATIONS,
    icon: Megaphone,
  },
];

const formatRole = (role?: string) =>
  role
    ? role
        .split('_')
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ')
    : 'Administrator';

export const SettingsPage: React.FC = () => {
  const { user, hasPermission, isDistrictAdmin } = usePermissions();
  const availableAreas = administrationAreas.filter(area =>
    hasPermission(area.permission)
  );
  const districtId = user?.district_id;
  const canManageDistrictSettings =
    isDistrictAdmin &&
    districtId !== undefined &&
    hasPermission(APP_PERMISSIONS.MANAGE_SYSTEM_SETTINGS);

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-blue-100 p-2 text-blue-700">
            <Settings className="h-6 w-6" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              Administration Settings
            </h2>
            <p className="mt-1 text-gray-600">
              Open the management areas available to your account.
            </p>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Administrator Account</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <p className="text-sm text-gray-500">Name</p>
            <p className="font-medium text-gray-900">
              {user?.full_name || user?.username || 'Not available'}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Email</p>
            <p className="font-medium text-gray-900">
              {user?.email || 'Not available'}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Role</p>
            <p className="font-medium text-gray-900">
              {formatRole(user?.role)}
            </p>
          </div>
        </CardContent>
      </Card>

      {canManageDistrictSettings && districtId !== undefined && (
        <>
          <AiScoringAssistSettings districtId={districtId} />
          <DataSharingConsentSettings districtId={districtId} />
        </>
      )}

      <div>
        <h3 className="text-lg font-semibold text-gray-900">
          Management Areas
        </h3>
        <p className="mt-1 text-sm text-gray-600">
          Each link opens a working, permission-controlled administration page.
        </p>
      </div>

      {availableAreas.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-gray-500">
            No additional management areas are assigned to this account.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {availableAreas.map(area => {
            const Icon = area.icon;
            return (
              <Link
                key={area.name}
                to={area.href}
                className="group rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                <Card className="h-full transition-shadow group-hover:shadow-md">
                  <CardContent className="flex h-full items-start gap-4 p-5">
                    <div className="rounded-lg bg-gray-100 p-2 text-gray-700">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-gray-900">{area.name}</p>
                      <p className="mt-1 text-sm text-gray-600">
                        {area.description}
                      </p>
                    </div>
                    <ChevronRight className="mt-1 h-5 w-5 text-gray-400 transition-transform group-hover:translate-x-1" />
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}

      <Card className="border border-amber-200 bg-amber-50">
        <CardContent className="p-5">
          <p className="font-medium text-amber-900">
            Additional system configuration
          </p>
          <p className="mt-1 text-sm text-amber-800">
            Evaluation-policy, notification-preference, and integration
            configuration are not yet persisted by the platform. Those
            controls are intentionally not shown.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsPage;
